// Member-facing gate entry.
//
// Until now triggerGate existed but nothing called it — the only way to
// exercise a member's entry was the admin Test Entry rehearsal. This is the
// screen that closes that gap.
//
// The TOTP code proves identity to the callable. It is generated here from the
// member's own secret (users/{uid}/private/gate, readable by them alone) rather
// than sent from the server, so the secret never travels on the wire per entry.
//
// Every decision about whether entry is allowed — membership standing, day-pass
// consumption, rate limiting — belongs to performMemberEntry on the server.
// This page only asks; it never decides.
import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import Layout from '../components/Layout';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { generateCode } from '../lib/totp';
import { logError } from '../utils/errorLogger';
import { useBranding } from '../contexts/BrandingContext';

const NAVY = '#0D1E35';
const GOLD = '#C9A84C';

// Reasons performMemberEntry can refuse, phrased for the person at the gate.
// Anything unlisted falls back to the raw reason so a new denial is never
// silently rendered as a blank screen.
const DENIAL_TEXT = {
  // Keys taken from functions/gate/eligibility.js and gate/index.js rather than
  // invented — a wrong key here silently degrades to the raw reason string.
  access_not_enabled: 'Gate access is not enabled on your account yet.',
  access_expired: 'Your gate access has expired.',
  membership_inactive: 'Your membership is not currently active.',
  day_pass_expired: 'Your day pass has expired.',
  day_pass_used: 'That day pass has already been used.',
  outside_allowed_hours: 'Outside your allowed hours for entry.',
  outside_allowed_days: 'Today is not one of your allowed days.',
  gate_disabled: 'The gate is temporarily disabled.',
  rate_limited_user: 'Too many attempts. Wait a moment and try again.',
  rate_limited_global: 'The gate is busy. Try again in a moment.',
  checkin_rule_not_yet_supported: 'Your access rule is not supported yet — ask the club.',
  invalid_code: 'Could not verify your device. Try again.',
  no_totp_secret: 'Gate access is not set up on this account yet.',
};

export default function MemberGate() {
  const branding = useBranding();
  const [secret, setSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { ok, message }
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setLoading(false); return; }
      try {
        const snap = await getDoc(doc(db, 'users', u.uid, 'private', 'gate'));
        setSecret(snap.exists() ? snap.data().totpSecret || null : null);
      } catch (e) {
        // Rules allow only the owner here; a failure means no access set up.
        setSecret(null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const open = useCallback(async () => {
    if (!secret || busy) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const code = await generateCode(secret);
      const fn = httpsCallable(getFunctions(), 'triggerGate');
      const { data } = await fn({ code });
      if (data?.ok) {
        setResult({ ok: true, message: 'Gate opening — come on in.' });
      } else {
        setResult({ ok: false, message: DENIAL_TEXT[data?.reason] || `Entry denied (${data?.reason || 'unknown'}).` });
      }
    } catch (e) {
      logError('MemberGate', e.message, e.stack);
      setError('Could not reach the gate. Check your signal and try again.');
    } finally {
      setBusy(false);
    }
  }, [secret, busy]);

  return (
    <Layout>
      <Box sx={{
        minHeight: '70vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center',
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: NAVY, mb: 1 }}>
          {branding.facilityName}
        </Typography>
        <Typography variant="body2" sx={{ color: '#888', mb: 4 }}>
          Open the gate from here when you arrive.
        </Typography>

        {loading ? (
          <CircularProgress sx={{ color: GOLD }} />
        ) : !secret ? (
          <Alert severity="info" sx={{ maxWidth: 420 }}>
            Gate access isn't set up on your account yet. Ask the club to enable it.
          </Alert>
        ) : (
          <>
            <Button
              onClick={open}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={22} sx={{ color: 'inherit' }} /> : <LockOpenIcon sx={{ fontSize: 32 }} />}
              sx={{
                width: 220, height: 220, borderRadius: '50%',
                backgroundColor: NAVY, color: GOLD,
                fontSize: '1.1rem', fontWeight: 700, letterSpacing: 1,
                flexDirection: 'column', gap: 1,
                '&:hover': { backgroundColor: '#16304f' },
                '&.Mui-disabled': { backgroundColor: '#16304f', color: 'rgba(201,168,76,0.5)' },
              }}
            >
              {busy ? 'Opening…' : 'Open Gate'}
            </Button>

            {result && (
              <Alert severity={result.ok ? 'success' : 'warning'} sx={{ mt: 4, maxWidth: 420 }}>
                {result.message}
              </Alert>
            )}
            {error && <Alert severity="error" sx={{ mt: 4, maxWidth: 420 }}>{error}</Alert>}

            <Typography variant="caption" sx={{ color: '#aaa', mt: 4, maxWidth: 380 }}>
              Every entry is logged. If the gate doesn't move within a few seconds,
              try once more before calling — the relay is a momentary pulse.
            </Typography>
          </>
        )}
      </Box>
    </Layout>
  );
}
